import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { authErrorMessage } from '../services/authErrors';
import { AuthShell } from './ui/AuthShell';

interface EmailVerificationProps {
  email: string | null;
  onRefresh: () => Promise<boolean>;
  onResend: () => Promise<void>;
  onSignOut: () => Promise<void>;
  deliveryFailed?: boolean;
}

export function EmailVerification({
  email,
  onRefresh,
  onResend,
  onSignOut,
  deliveryFailed = false,
}: EmailVerificationProps) {
  const [pending, setPending] = useState<
    'refresh' | 'resend' | 'signout' | null
  >(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setTimeout(
      () => setResendCooldown((seconds) => seconds - 1),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const run = async (action: 'refresh' | 'resend' | 'signout') => {
    setPending(action);
    setMessage(null);
    setError(null);
    try {
      if (action === 'refresh') {
        const verified = await onRefresh();
        if (!verified)
          setMessage(
            'That email is not verified yet. Open the link in your email, then try again.',
          );
      } else if (action === 'resend') {
        await onResend();
        setResendCooldown(60);
        setMessage(
          'Verification email sent. Check your inbox and spam folder.',
        );
      } else {
        await onSignOut();
      }
    } catch (cause) {
      setError(authErrorMessage(cause));
    } finally {
      setPending(null);
    }
  };

  return (
    <AuthShell title="Verify your email">
      <Box sx={{ textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Verify <strong>{email || 'your email address'}</strong> before opening
          your qkIndex data. If the email has not arrived, resend it below.
        </Typography>
        {deliveryFailed && !message && !error && (
          <Alert severity="warning" role="alert" sx={{ mb: 2 }}>
            Your account was created, but the verification email could not be
            sent. Resend it below.
          </Alert>
        )}
        {message && (
          <Alert severity="info" aria-live="polite" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          variant="contained"
          fullWidth
          disabled={pending !== null}
          onClick={() => {
            void run('refresh');
          }}
          sx={{ mb: 1.5 }}
        >
          {pending === 'refresh' ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "I've verified my email"
          )}
        </Button>
        <Button
          fullWidth
          disabled={pending !== null || resendCooldown > 0}
          onClick={() => {
            void run('resend');
          }}
          sx={{ mb: 1 }}
        >
          {pending === 'resend'
            ? 'Sending…'
            : resendCooldown > 0
              ? `Resend available in ${resendCooldown}s`
              : 'Resend verification email'}
        </Button>
        <Button
          fullWidth
          disabled={pending !== null}
          onClick={() => {
            void run('signout');
          }}
        >
          {pending === 'signout' ? 'Signing out…' : 'Use another account'}
        </Button>
      </Box>
    </AuthShell>
  );
}
